import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

const STORAGE_KEY = 'tacita.install.dismissedAt';
/** The client has already made a decision about installing — we do not push a second time. */
const ATTEMPT_KEY = 'tacita.install.attemptedAt';
const RE_PROMPT_DAYS = 7;

// MODULE scope on purpose. `beforeinstallprompt` fires once, early, and only the listener alive at that moment
// receives the event — that is the shell's instance. Per-call state meant a second caller (the settings row)
// always saw installable=false and could never trigger the native prompt. One shared state, one set of
// listeners, every caller sees the same truth.
const installable = ref(false);                  // Android Chrome captured beforeinstallprompt
const isIos = ref(false);                        // iOS — must use Share → Add to Home Screen
const isAndroid = ref(false);
/** Install only exists on mobile for us: there is no desktop install path at all (neither sheet nor setting). */
const isMobile = ref(false);
const isStandalone = ref(false);                 // already installed
/** The install sheet lives in the shell; anything (e.g. Settings) can raise it through this. */
const sheetOpen = ref(false);
/**
 * Full-screen layers that sit above the shell («Ваша карта готова», CardIssuedReveal) hold the sheet while they are up:
 * the post-issuance prompt fires exactly when that layer appears, and a sheet opened under it could be neither seen
 * nor closed (install-entry-points E2E, 01.11). Held, the request waits and the sheet appears once the layer is gone.
 */
const sheetHolds = ref(0);
const sheetVisible = computed(() => sheetOpen.value && sheetHolds.value === 0);
let deferred: BeforeInstallPromptEvent | null = null;
let listenersBound = false;

/**
 * Storage access is wrapped everywhere in this file, without exception.
 *
 * In private-mode Safari and whenever the browser blocks site data, `setItem` THROWS. The attempt marker is
 * written right before `deferred.prompt()`, so an unguarded write would kill the install itself rather than
 * just the memory of it. Degrading towards "show the sheet once too often" is incomparably cheaper.
 */
function readStorage(key: string): string | null {
  try { return window.localStorage.getItem(key); }
  catch { return null; }
}

function writeStorage(key: string, value: string) {
  try { window.localStorage.setItem(key, value); }
  catch { /* no memory of it — the install matters more than the marker */ }
}

/**
 * The «Add to Home Screen» steps for this iPhone, as i18n keys. Safari 26 hid Share behind «…» right of the
 * address, Safari 27 behind «☰» left of it, and both add «View More» and a «Save» that waits for the icon
 * (owner, 02.10). The Safari version comes from `Version/NN` — iOS 26 froze the OS part of the UA at 18_x, so it
 * cannot tell 26 from 18. Other iOS browsers (Chrome, Firefox) have their own toolbars: they get the generic steps.
 */
export function iosInstallSteps(ua: string): string[] {
  const safari = /Safari\//.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser/.test(ua);
  const major = Number(ua.match(/Version\/(\d+)/)?.[1] ?? 0);
  if (!safari || major < 26) return ['install.iosStep1', 'install.iosStep2', 'install.iosStep3'];
  return [
    major >= 27 ? 'install.ios27.open' : 'install.ios26.open',
    'install.ios26.more', 'install.ios26.addHome', 'install.ios26.save',
  ];
}

export function useInstallPrompt() {

  function detect() {
    if (typeof window === 'undefined') return;
    const ua = window.navigator.userAgent;
    const iosUa = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
    // iPadOS 13+ reports itself as Macintosh — by UA alone it is indistinguishable from a desktop. Since we
    // offer no install on macOS at all, such an iPad would silently lose it. Touch input tells them apart.
    const iPadAsMac = /Macintosh/.test(ua) && window.navigator.maxTouchPoints > 1;
    isIos.value = iosUa || iPadAsMac;
    isAndroid.value = /Android/.test(ua);
    isMobile.value = isIos.value || isAndroid.value;
    const mediaStandalone =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches;
    const iosStandalone = (window.navigator as any).standalone === true;
    isStandalone.value = mediaStandalone || iosStandalone;
  }

  function onBeforeInstall(event: Event) {
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    installable.value = true;
  }

  function onInstalled() {
    deferred = null;
    installable.value = false;
    isStandalone.value = true;
  }

  async function triggerNativePrompt(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    if (!deferred) return 'unavailable';
    // Marked on the TAP, not on a successful install: someone who opened the system dialog and changed their
    // mind has made their decision, and there is no point reminding them a second time.
    rememberAttempt();
    await deferred.prompt();
    const choice = await deferred.userChoice;
    deferred = null;
    installable.value = false;
    return choice.outcome;
  }

  /**
   * iOS: open the system share sheet, from which the client picks "Add to Home Screen". No app modal appears.
   *
   * Caveat worth knowing: Safari lists "Add to Home Screen" in ITS OWN share sheet (the toolbar button); a
   * sheet opened from the page via `navigator.share` carries the ordinary set of actions and may not include
   * it. If it turns out missing on a real device, the settings row keeps a one-line hint underneath — still
   * without a modal.
   */
  async function openShareSheet(): Promise<void> {
    rememberAttempt();
    try {
      await window.navigator.share?.({ title: 'Tacita', url: window.location.origin });
    } catch { /* the client closed the sheet — not an error */ }
  }

  function shouldShow(): boolean {
    if (isStandalone.value) return false;
    const last = Number(readStorage(STORAGE_KEY) ?? '0');
    if (!last) return true;
    const daysSince = (Date.now() - last) / (1000 * 60 * 60 * 24);
    return daysSince >= RE_PROMPT_DAYS;
  }

  function snooze() {
    writeStorage(STORAGE_KEY, String(Date.now()));
  }

  /** The client already made a decision about installing — do not push a second time. */
  function installAttempted(): boolean {
    // Private mode — treat as "never tapped": showing once more is the safe direction to fail in.
    return !!readStorage(ATTEMPT_KEY);
  }

  function rememberAttempt() {
    writeStorage(ATTEMPT_KEY, String(Date.now()));
  }

  /** A full-screen layer is up (true) or gone (false); the sheet shows only while nothing holds it. */
  function holdSheet(held: boolean) {
    sheetHolds.value = Math.max(0, sheetHolds.value + (held ? 1 : -1));
  }

  /** Raise the install sheet from anywhere (Settings row), not just the shell's timed prompt. */
  function openSheet() {
    detect();                                   // standalone/iOS can change between visits — re-read on demand
    sheetOpen.value = true;
  }

  onMounted(() => {
    detect();
    // Bound once for the whole app: a second binding would capture the same event twice and, worse, the
    // unmount of any single caller would tear the listeners down for everyone.
    if (!listenersBound) {
      window.addEventListener('beforeinstallprompt', onBeforeInstall);
      window.addEventListener('appinstalled', onInstalled);
      listenersBound = true;
    }
  });

  onBeforeUnmount(() => { /* listeners are app-wide — see above */ });

  return {
    installable, isIos, isAndroid, isMobile, isStandalone, sheetOpen, sheetVisible, holdSheet,
    openSheet, openShareSheet, triggerNativePrompt, shouldShow, snooze,
    installAttempted, rememberAttempt,
  };
}
